import moment from "moment";
import React, { useState } from "react";
import { useEffect, useRef } from "react";
import WeekEvents from "./WeekEvents";
import ShowInfoAfspraak from "./ShowInfoAfspraak";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/20/solid";
import { generateDaysArrayForWeek } from "./calenderUtilties";
import CrudEventCalendly from "./CrudEventCalendly";
import CrudEventCalendlyPatienten from "./CrudEventCalendlyPatienten";

// components/Day.js

const Header = ({ formattedMonth, currentMonth, setOpen }) => (
  <header className="border-b border-wa-border bg-wa-panel px-4 py-3 sm:px-6 lg:flex-none">
    <div className="flex items-center justify-between">
      <h1 className="text-lg font-semibold leading-6 text-wa-text">
        <time dateTime={currentMonth}>
          {moment(formattedMonth).locale("nl-be").format("MMMM YYYY")}
        </time>
      </h1>
      <div className="hidden md:ml-4 md:flex md:items-center">
        <div className="ml-6 h-6 w-px bg-wa-border" />
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="ml-6 rounded-md bg-wa-accent px-3 py-2 text-sm font-semibold text-wa-onaccent shadow-sm hover:bg-wa-accentdeep focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-wa-accent"
        >
          Afspraak maken
        </button>
      </div>
    </div>
    <button
      type="button"
      onClick={() => setOpen(true)}
      className="mt-3 inline-flex w-full items-center justify-center rounded-md bg-wa-accent px-3 py-2 text-sm font-semibold text-wa-onaccent shadow-sm hover:bg-wa-accentdeep focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-wa-accent md:hidden"
    >
      Afspraak maken
    </button>
  </header>
);

const HeaderDays = ({ days, handlePreviousWeek, handleNextWeek }) => {
  return (
    <div className="mt-2 grid h-full grid-cols-7 divide-x divide-wa-border border-r border-wa-border text-[11px] leading-6 text-wa-muted sm:text-sm">
      <div className="col-end-1 w-14" />
      {days.map((day, index) => (
        <div
          key={index}
          className="flex items-center justify-center gap-x-1 py-2 sm:py-3"
        >
          {index == 0 && (
            <button
              type="button"
              onClick={handlePreviousWeek}
              className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-wa-muted hover:text-wa-text"
            >
              <span className="sr-only">Previous month</span>
              <ChevronLeftIcon className="h-7 w-7" aria-hidden="true" />
            </button>
          )}
          <span className="items-center justify-center font-semibold text-wa-text">
            <span className="sm:hidden">{String(day.titleDay).slice(0, 2)}</span>
            <span className="hidden sm:inline">{day.titleDay}</span>
          </span>
          <span className="items-center justify-center font-semibold text-wa-text">
            {day.numDay}
          </span>
          {index == 6 && (
            <button
              type="button"
              onClick={handleNextWeek}
              className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-wa-muted hover:text-wa-text"
            >
              <span className="sr-only">Previous month</span>
              <ChevronRightIcon
                className="h-7 w-7"
                aria-hidden="true"
              />
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

const HoursLines = ({ containerOffset }) => {
  const hours = Array.from({ length: 15 }, (_, i) => {
    const acht = i + 8;
    const hour = Math.floor(acht).toString().padStart(2, "0"); // Get the full hour
    const minutes = "00"; // Alternate between ":00" and ":30"
    return `${hour}:${minutes}`; // Combine hour and minutes
  });

  return (
    <div
      className="col-start-1 col-end-2 row-start-1 grid divide-y divide-wa-border bg-wa-panel2 text-wa-text"
      style={{ gridTemplateRows: "repeat(15, minmax(8rem, 1fr))" }}
    >
      <div
        ref={containerOffset}
        className="row-end-1 h-7 bg-wa-panel"
      ></div>
      {hours.map((hour, index) => (
        <React.Fragment key={index}>
          <div>
            <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-wa-muted">
              <span className="text-sm">{hour}</span>
            </div>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

const DaysLines = () => {
  const numColumns = 8; // Change this to dynamically adjust the number of columns

  return (
    <div
      className="col-start-1 col-end-2 row-start-1 grid grid-cols-7 grid-rows-1 divide-x divide-wa-border"
    >
      {Array.from({ length: numColumns }, (_, index) => (
        <div
          key={index}
          className={`col-start-${index + 1} row-span-full ${
            index === numColumns - 1 ? "w-8" : ""
          }`}
        />
      ))}
    </div>
  );
};

const Schedule = ({
  containerNav,
  container,
  containerOffset,
  days,
  showAfspraak,
  handleNextWeek,
  handlePreviousWeek,
}) => (
  <div
    ref={container}
    className="isolate flex flex-auto flex-col overflow-auto bg-wa-panel2"
  >
    <div
      className="flex w-[62rem] max-w-none flex-none flex-col xl:w-full xl:max-w-full"
    >
      <div
        ref={containerNav}
        className="sticky top-0 z-30 flex-none bg-wa-panel2 shadow ring-1 ring-wa-border sm:pr-8"
      >
        <HeaderDays
          days={days}
          handleNextWeek={handleNextWeek}
          handlePreviousWeek={handlePreviousWeek}
        />
      </div>
      <div className="flex flex-auto bg-wa-panel2">
        <div className="sticky left-0 z-10 w-14 flex-none bg-wa-panel2 ring-1 ring-wa-border" />
        <div className="grid flex-auto grid-cols-1 grid-rows-1 bg-wa-panel2">
          <HoursLines containerOffset={containerOffset} />
          <DaysLines />
          {days?.length > 0 &&
            days?.map((item) => (
              <WeekEvents showAfspraak={showAfspraak} evenmentes={item?.events} />
            ))}
        </div>
      </div>
    </div>
  </div>
);

const WeekView = ({ userdetails, user, select, appointments }) => {
  const [open, setOpen] = useState(false);
  const [afspraak, setAfspraak] = useState(null);
  const [openModalEdit, setOpenModalEdit] = useState(false);
  const [currentDate, setCurrentDate] = useState(moment().locale("nl-be")); // Current week starting date
  const [days, setDays] = useState(
    generateDaysArrayForWeek(currentDate, null, appointments)
  );

  const handlePreviousWeek = () => {
    const newDate = currentDate.clone().subtract(1, "weeks");
    setCurrentDate(newDate); // Update the current week
  };

  // Handle moving to the next week
  const handleNextWeek = () => {
    const newDate = currentDate.clone().add(1, "weeks");
    setCurrentDate(newDate); // Update the current week
  };

  useEffect(() => {
    setDays(generateDaysArrayForWeek(currentDate, null, appointments));
  }, [currentDate, appointments]);

  const container = useRef(null);
  const containerNav = useRef(null);
  const containerOffset = useRef(null);

  const showAfspraak = (afspr) => {
    setAfspraak(afspr);
    setOpenModalEdit(true);
  };

  useEffect(() => {
    const now = new Date();
    const currentMinute = now.getHours() * 60 + now.getMinutes();
    const scrollable =
      container.current.scrollHeight -
      containerNav.current.offsetHeight -
      containerOffset.current.offsetHeight;
    container.current.scrollTop =
      scrollable * Math.max(0, Math.min(1, (currentMinute - 480) / 900));
  }, []);

  return (
    <div className="flex h-full flex-col rounded-lg border border-wa-border bg-wa-panel text-wa-text">
      <Header
        formattedMonth={currentDate}
        setOpen={setOpen}
        currentMonth={currentDate.format("YYYY-MM")}
      />
      <div className="border-b border-wa-border bg-wa-panel2 px-4 py-2 text-xs text-wa-muted md:hidden">
        Swipe horizontaal om alle dagen te zien.
      </div>
      <div className="hidden border-b border-wa-border bg-wa-panel2 px-4 py-2 text-xs text-wa-muted md:block xl:hidden">
        Tablet tip: schuif horizontaal om de volledige weektijdlijn te bekijken.
      </div>
      <Schedule
        container={container}
        containerNav={containerNav}
        containerOffset={containerOffset}
        days={days}
        showAfspraak={showAfspraak}
        appointments={appointments}
        handlePreviousWeek={handlePreviousWeek}
        handleNextWeek={handleNextWeek}
      />
       {user?.role == 2 ? (
        <CrudEventCalendly openModel={open} setOpenModel={setOpen} />
      ) : (
        <CrudEventCalendlyPatienten openModel={open} setOpenModel={setOpen} />
      )}
      {afspraak && (
        <ShowInfoAfspraak
          open={openModalEdit}
          setOpen={setOpenModalEdit}
          select={select}
          evenment={afspraak}
          user={user}
        />
      )}
    </div>
  );
};

export default WeekView;
